import { GetStaticPropsContext } from 'next';

import { PageCategory } from '~/features/analytics/constants';
import { ExperiencePromoBannerSection } from '~/features/claude-engineer-2026/components/experience-promo-banner-section';
import {
  ExperienceAtfHeroSection,
  StaticExperienceAtfHeroSectionProps,
  transformStaticExperienceAtfHeroSectionProps,
} from '~/features/experience/components/experience-atf-hero-section';
import {
  ExperienceIndustryLeadersSection,
  StaticExperienceIndustryLeadersSectionProps,
  transformStaticExperienceIndustryLeadersSectionProps,
} from '~/features/experience/components/experience-industry-leaders-section';
import {
  ExperiencePlansSection,
  StaticExperiencePlansSectionProps,
  transformStaticExperiencePlansSectionProps,
} from '~/features/experience/components/experience-plans-section';
import {
  ExperienceSelectedProgramsSection,
  StaticExperienceSelectedProgramsSectionProps,
  transformStaticExperienceSelectedProgramsSectionProps,
} from '~/features/experience/components/experience-selected-programs-section';
import {
  ExperienceTestimonialsSection,
  StaticExperienceTestimonialsSectionProps,
  transformStaticExperienceTestimonialsSectionProps,
} from '~/features/experience/components/experience-testimonials-section';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { RplHomepageSection } from '~/features/recognition-of-prior-learning/components/rpl-homepage-section';
import { getSubscriptionSeoPriceData } from '~/features/seo/controllers/get-subscription-seo-price-data';
import {
  createSubscriptionStructuredData,
  serializeSubscriptionStructuredData,
} from '~/features/seo/utils/create-subscription-structured-data';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

const PAGE_URL = 'https://www.udacity.com/experience';

export default function ExperiencePage({
  page,
  staticExperienceAtfHeroSectionProps,
  staticExperienceTestimonialsSectionProps,
  staticExperiencePlansSectionProps,
  staticExperienceIndustryLeadersSectionProps,
  staticExperienceSelectedProgramsSectionProps,
  structuredData,
}: {
  page: Page;
  staticExperienceAtfHeroSectionProps: StaticExperienceAtfHeroSectionProps;
  staticExperienceTestimonialsSectionProps: StaticExperienceTestimonialsSectionProps;
  staticExperiencePlansSectionProps: StaticExperiencePlansSectionProps;
  staticExperienceIndustryLeadersSectionProps: StaticExperienceIndustryLeadersSectionProps;
  staticExperienceSelectedProgramsSectionProps: StaticExperienceSelectedProgramsSectionProps;
  structuredData: string;
}) {
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Experience,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <ExperienceAtfHeroSection {...staticExperienceAtfHeroSectionProps} />
        <ExperiencePromoBannerSection />
        <RplHomepageSection eventName='ExperiencePage CTA Clicked' />
        <ExperienceSelectedProgramsSection {...staticExperienceSelectedProgramsSectionProps} />
        <ExperienceIndustryLeadersSection {...staticExperienceIndustryLeadersSectionProps} />
        <ExperienceTestimonialsSection {...staticExperienceTestimonialsSectionProps} />
        <ExperiencePlansSection {...staticExperiencePlansSectionProps} />
      </PageLayout>
      <script dangerouslySetInnerHTML={{ __html: structuredData }} type='application/ld+json' />
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page] = await Promise.all([getPage('/experience')]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const staticExperienceAtfHeroSectionProps = transformStaticExperienceAtfHeroSectionProps();
  const staticExperienceTestimonialsSectionProps = transformStaticExperienceTestimonialsSectionProps();
  const staticExperiencePlansSectionProps = transformStaticExperiencePlansSectionProps();
  const staticExperienceIndustryLeadersSectionProps = await transformStaticExperienceIndustryLeadersSectionProps();
  const staticExperienceSelectedProgramsSectionProps = await transformStaticExperienceSelectedProgramsSectionProps();

  const priceData = await getSubscriptionSeoPriceData();
  const structuredData = serializeSubscriptionStructuredData(
    createSubscriptionStructuredData({
      url: PAGE_URL,
      name: 'Udacity Subscription',
      description: page.seo.description,
      breadcrumbName: 'Experience',
      priceData,
    })
  );

  return {
    props: {
      page,
      staticExperienceAtfHeroSectionProps,
      staticExperienceTestimonialsSectionProps,
      staticExperiencePlansSectionProps,
      staticExperienceIndustryLeadersSectionProps,
      staticExperienceSelectedProgramsSectionProps,
      structuredData,
    },
  };
}
